import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { MachineDocumentState } from "./models/machineDocumentsModel";
import customerMachineDocumentApi from "./api/customerMachineDocumentApi";

export const getDocuments = createAsyncThunk("customerDocuments/getDocuments", async (machineId: string) => {
  return customerMachineDocumentApi.getDocuments(machineId);
});

export const getDocument = createAsyncThunk("customerMachineDocuments/getDocument", async (documentId: string) => {
  return customerMachineDocumentApi.getDocument(documentId);
});

export const createDocument = createAsyncThunk(
  "customerMachineDocuments/createDocument",
  async ({ machineId, model }: any) => {
    return customerMachineDocumentApi.createDocument(machineId, model);
  }
);

export const changeDocument = createAsyncThunk(
  "customerMachineDocuments/changeDocument",
  async ({ documentId, model }: any) => {
    return customerMachineDocumentApi.changeDocument(documentId, model);
  }
);

// NO public/anonymous document exposure — owner ruling 2026-08-13 (D14): documents
// are login-only. Do not re-add a visibility tick or a public docs list without a new
// owner ruling. The `setDocumentVisibility` thunk (and its optimistic pending/rejected
// reducers) lived here; its B7-BE5 endpoint is deleted server-side, so a re-added call
// would 404.

export const deleteDocument = createAsyncThunk(
  "customerMachineDocuments/deleteDocument",
  async (documentId: string) => {
    return customerMachineDocumentApi.deleteDocument(documentId);
  }
);

const initialState: MachineDocumentState = {
  documents: undefined,
  document: undefined,
  loading: false,
  success: undefined,
  error: undefined
};

const customerMachineDocumentsSlice = createSlice({
  name: "customerMachineDocuments",
  initialState: initialState,
  reducers: {
    selectDocument: (state, action) => {
      state.document = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDocuments.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDocuments.fulfilled, (state, { payload }) => {
        state.documents = payload;
        state.loading = false;
      })
      .addCase(getDocuments.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getDocument.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDocument.fulfilled, (state, { payload }) => {
        state.document = payload;
        state.loading = false;
      })
      .addCase(getDocument.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(createDocument.pending, (state) => {
        state.loading = true;
      })
      .addCase(createDocument.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(createDocument.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(changeDocument.pending, (state) => {
        state.loading = true;
      })
      .addCase(changeDocument.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(changeDocument.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });
  }
});

export const {selectDocument} = customerMachineDocumentsSlice.actions;

export default customerMachineDocumentsSlice.reducer;